/**
 * Consumer-Friendly Product Metadata (ProSource Labs catalog)
 * -----------------------------------------------------------
 * Every product has TRULY INDIVIDUALIZED prose written in plain-English,
 * benefit-driven voice — no clinical jargon dumps, no molecular weights
 * or receptor biology in the customer-facing copy.
 *
 * Voice guide (per store owner, 2026-02):
 * · Short, punchy opening — what it IS and what it's KNOWN FOR
 * · keyFacts written as "Bold Label: benefit description" bullets
 * · Talk to a curious buyer, not a research chemist
 * · Absolutely NO one-size-fits-all boilerplate
 * · Compliance-safe: research-use framing, no dosing / medical claims
 *
 * Renders inside ProductLongContent.jsx. Every prod-catalog slug MUST
 * have a matching key here — otherwise the PDP falls to a generic
 * template and the customer sees blanket boilerplate.
 */

/* eslint-disable no-unused-vars */
export const PRODUCT_META = {
 /* ─────────── HEALING & RECOVERY ─────────── */
 "bpc-157": {
 form: "peptide",
 fullName: "BPC-157 (Body Protection Compound-157)",
 overview: `BPC-157 is a research peptide derived from a small protein naturally
 found in stomach juice. It's one of the most-studied healing peptides on the
 market and is best known for helping tissues bounce back faster from wear-and-tear,
 injury, and inflammation. Because it's so stable it can be handled and stored more
 easily than most peptides its size.`,
 researchApplication: `Widely used in research tissue-repair research — tendons,
 ligaments, muscle, and the gut lining are the most-studied targets. Researchers
 also pair BPC-157 with TB-500 in the well-known "Wolverine stack" for combined
 soft-tissue and systemic-repair studies.`,
 keyFacts: [
 "Localized Healing: Targets tendons, ligaments, muscles, and joint tissues in study models",
 "Gut Support: Well-known for protecting and repairing stomach and intestinal lining in studies",
 "Blood Flow: Encourages new blood-vessel formation to bring oxygen and nutrients to injured spots",
 "Stability: Unusually stable for a peptide its size — resists rapid breakdown",
 ],
  },

 "tb500": {
 form: "peptide",
 fullName: "TB-500 (Thymosin β4 Research Peptide)",
 overview: `TB-500 is the research market's name for Thymosin β4 — a naturally occurring
 peptide your body already uses to help cells move to injured areas and repair
 themselves. It travels systemically, which is why researchers reach for TB-500 when
 they want whole-body repair effects rather than one localized spot.`,
 researchApplication: `Common in research studies on cell migration, wound closure,
 cardiac ischemia recovery, corneal repair, and hair-follicle activation. Frequently
 combined with BPC-157 in the classic "Wolverine stack" for complementary
 localized + systemic repair coverage.`,
 keyFacts: [
 "Systemic Repair: Moves through the body to support widespread muscle and tissue recovery",
 "Cell Migration: Encourages cells to travel to injury sites for faster wound closure",
 "Less Scarring: Studied for reducing fibrous scar-tissue formation during healing",
 "Pairs Well: Standard partner peptide to BPC-157 in the 'Wolverine' repair stack",
 ],
  },

 "bpc-157-tb-500": {
 form: "blend",
 fullName: "BPC-157 + TB-500 Blend (\"Wolverine Stack\")",
 overview: `BPC-157 and TB-500 are often combined into what researchers nickname the
 "Wolverine stack" — a two-in-one repair kit that pairs localized tissue healing
 with body-wide recovery support. This SKU pre-blends both peptides in a single
 vial so the ratio stays consistent across study replicates.`,
 researchApplication: `The go-to research preparation when a study needs both
 complementary repair pathways at once — localized joint/tendon/gut repair from
 BPC-157 plus systemic muscle and tissue recovery from TB-500. Saves reconstitution
 time and keeps dosing consistent across experiments.`,
 keyFacts: [
 "Fast Healing: Combined action promotes quicker recovery from soft-tissue injuries in study models",
 "Localized + Systemic: BPC-157 targets specific injuries while TB-500 supports body-wide repair",
 "Lower Inflammation: Both peptides studied for calming inflammation at injury sites",
 "Convenience: Pre-blended into one vial — no separate reconstitution required",
 ],
  },

 "ghk-cu": {
 form: "peptide",
 fullName: "GHK-Cu (Copper Tripeptide-1)",
 overview: `GHK-Cu is the naturally occurring copper-carrying peptide your body already
 produces — the levels of which decline noticeably with age. It's a favorite in skin,
 hair, and connective-tissue research because of how many different repair pathways
 it seems to touch at once.`,
 researchApplication: `Extensively studied in research skin-regeneration, wound-healing,
 collagen-production, and hair-follicle research. Also appears in tissue-remodeling
 and anti-fibrotic study models.`,
 keyFacts: [
 "Skin Regeneration: One of the most-studied peptides for skin repair, collagen, and elastin production",
 "Hair Follicles: Studied for stimulating dormant follicles and improving hair density in study models",
 "Copper Delivery: Naturally binds and delivers copper — essential for many repair enzymes",
 "Anti-Aging Research: Widely used in studies on the aging-repair pathway",
 ],
  },

 /* ─────────── GH / GROWTH-HORMONE FAMILY ─────────── */
 "ipamorelin": {
 form: "peptide",
 fullName: "Ipamorelin (Selective GH Releaser)",
 overview: `Ipamorelin is one of the cleanest, most selective growth-hormone-releasing
 research peptides available. It nudges the body to release its own growth hormone
 without the extra unwanted signaling (like cortisol or hunger) that older peptides
 in this family caused. That selectivity is what made it a research favorite.`,
 researchApplication: `Most-used in research GH-axis studies where researchers want
 isolated growth-hormone effects without confounding stress-hormone or appetite
 changes. Commonly paired with CJC-1295 (no DAC) in the classic GHRH + GHRP
 research combo.`,
 keyFacts: [
 "Clean Profile: No significant cortisol or prolactin spikes — a big advantage over older GHRPs",
 "Appetite Neutral: Doesn't trigger hunger the way ghrelin-mimicking peptides do",
 "Short & Selective: Just 5 amino acids, engineered for one specific receptor",
 "Stacks Well: Standard pair with CJC-1295 (no DAC) for pulsed GH-release studies",
 ],
  },

 "cjc-1295-no-dac": {
 form: "peptide",
 fullName: "CJC-1295 without DAC (Mod-GRF 1-29)",
 overview: `CJC-1295 without DAC — also known as "Mod-GRF 1-29" — is the short-acting
 version of the CJC-1295 growth-hormone releaser. It stays active in the body for
 minutes rather than days, which mimics your natural pulsed GH release cycle. That's
 why it's the version researchers reach for when they want a natural-feeling pulse
 rather than a sustained flood.`,
 researchApplication: `The go-to pairing partner for Ipamorelin or GHRP-6 in research
 GH-axis studies where a pulsatile (peak-and-recover) GH-release pattern is required.
 Better mimics how the body's own hypothalamus signals for growth hormone.`,
 keyFacts: [
 "Pulsed Release: Short-acting — mimics your body's natural GH-release rhythm",
 "Fast Clearance: Half-life ~30 minutes, so it comes and goes cleanly",
 "Stack Partner: Standard companion to Ipamorelin in the classic GHRH + GHRP combo",
 "Also Called: Frequently listed in the literature as 'Mod-GRF 1-29'",
 ],
  },

 "cjc-1295-with-dac": {
 form: "peptide",
 fullName: "CJC-1295 with DAC (Long-Acting Variant)",
 overview: `CJC-1295 with DAC is the long-acting cousin of Mod-GRF 1-29. The "DAC"
 attachment lets it bind onto blood proteins and stick around in circulation for
 DAYS instead of minutes. This makes it a totally different research tool — it
 produces steady, elevated GH signaling rather than short pulses, which lets studies
 run on a once-a-week rhythm.`,
 researchApplication: `Used in studies when the study question calls for
 sustained, tonic GH-axis stimulation instead of pulsatile signals. Choosing between
 DAC and no-DAC is one of the biggest experimental-design decisions in this
 research area.`,
 keyFacts: [
 "Long-Acting: Multi-day half-life in study models — one dose covers days",
 "Sustained Signal: Delivers steady GH release rather than natural pulses",
 "Weekly Cadence: Convenient dosing schedule for long-duration study designs",
 "Different Tool: Not interchangeable with the no-DAC form — the way it moves through the body is totally different",
 ],
  },

 "cjc-1295-nodac-ipamorelin": {
 form: "blend",
 fullName: "CJC-1295 (no DAC) + Ipamorelin Blend",
 overview: `This is the classic "one vial, one shot" research kit — CJC-1295 without
 DAC and Ipamorelin pre-blended in a single vial. It's the most-requested combination
 in the growth-hormone research space because the two peptides work on different
 pathways to the same goal: stimulating the body's natural GH pulse.`,
 researchApplication: `Standard research GHRH + GHRP co-administration preparation.
 Combines pulsatile GHRH-receptor activity (CJC no-DAC) with clean, selective
 GHS-R1a activation (Ipamorelin) so both receptors fire together for a synergistic
 GH release.`,
 keyFacts: [
 "Two Peptides, One Vial: Simpler prep, consistent ratio across every study run",
 "Natural Pulse: Together they push GH release without spiking cortisol or hunger",
 "Best of Both: GHRH pathway + ghrelin receptor pathway — activated simultaneously",
 "Research Favorite: Most-purchased blend in the GH-research category",
 ],
  },

 "ghrp-2": {
 form: "peptide",
 fullName: "GHRP-2 (Pralmorelin)",
 overview: `GHRP-2 is the most potent of the classic first-generation growth-hormone-
 releasing peptides — the "OG" of this research space. It works by mimicking ghrelin
 (your body's hunger hormone) at its receptor, which is what drives the strong GH-
 release signal. That potency comes with a caveat: unlike Ipamorelin, it does bump
 cortisol and hunger a bit alongside GH.`,
 researchApplication: `Research work on ghrelin-receptor biology, GH pulse
 magnitude, and appetite/food-intake brain circuits. Used in Japanese diagnostic
 literature as a probe for GH-deficient patient identification.`,
 keyFacts: [
 "Strongest Classic GHRP: The most potent of the first-generation growth-hormone releasers",
 "Ghrelin Mimic: Activates the same receptor as your natural hunger hormone",
 "Hunger Bump: Does mildly stimulate appetite — not appetite-neutral like Ipamorelin",
 "Also Called: Known as 'Pralmorelin' in older diagnostic-medicine literature",
 ],
  },

 "ghrp-6": {
 form: "peptide",
 fullName: "GHRP-6",
 overview: `GHRP-6 is the appetite-stimulating cousin of GHRP-2 — the ghrelin-mimicking
 research peptide most known for driving a noticeable hunger response along with its
 growth-hormone release. That hunger side effect is exactly what makes it useful in
 certain research designs (appetite / cachexia studies) even though it's a downside
 in others.`,
 researchApplication: `Research models of appetite stimulation, cachexia recovery,
 and GH-axis studies where hunger response is either the outcome of interest or an
 acceptable side effect.`,
 keyFacts: [
 "Strong Appetite Response: Most hunger-inducing of the classic GHRP family",
 "Ghrelin Mimic: Activates GHS-R1a — the same receptor as ghrelin",
 "GH Release: Reliable growth-hormone releaser in study models",
 "Cachexia Research: Common tool compound in muscle-wasting recovery research",
 ],
  },

 "hgh-191aa": {
 form: "protein",
 fullName: "HGH 191aa (Recombinant Human Growth Hormone)",
 overview: `HGH 191aa is the full 191-amino-acid recombinant version of human growth
 hormone — the same molecular structure as what your pituitary gland makes naturally.
 Unlike the smaller "releaser" peptides that nudge your body to make its own GH, this
 is the finished hormone itself, ready to work directly.`,
 researchApplication: `Research studies on the GH/IGF-1 axis, muscle protein
 synthesis, adipose tissue metabolism, and tissue-repair biology where direct GH
 administration (rather than pulse stimulation) is the study design.`,
 keyFacts: [
 "Full Molecule: The complete 191-amino-acid GH protein, not a fragment or releaser",
 "Direct Action: Works immediately without needing your pituitary to respond",
 "Standard Reference: The comparison point for all other GH-releasing research peptides",
 "Cold Chain Required: Must ship cold and stay refrigerated — do not room-temp",
 ],
  },

 "tesamorelin": {
 form: "peptide",
 fullName: "Tesamorelin",
 overview: `Tesamorelin is a stabilized version of your body's natural growth-hormone-
 releasing hormone (GHRH). It's the FDA-approved-for-research GHRH analog most
 known for its role in visceral fat and abdominal-adiposity studies — that's where
 the bulk of its research and clinical literature sits.`,
 researchApplication: `Research and clinical studies on visceral fat reduction,
 abdominal adiposity, IGF-1 axis dynamics, and lipodystrophy models. The most-cited
 GHRH analog in the fat-loss / body-composition research space.`,
 keyFacts: [
 "Visceral Fat Focus: Best-studied GHRH analog for abdominal / deep-belly fat research",
 "Stabilized GHRH: Engineered to last longer than your natural GHRH signal",
 "Rich Literature: Extensive clinical trials in HIV-lipodystrophy body-comp studies",
 "IGF-1 Effects: Drives measurable IGF-1 elevations in study protocols",
 ],
  },

 /* ─────────── GLP-1 / METABOLIC ─────────── */
 "semaglutide": {
 form: "peptide",
 fullName: "Semaglutide (GLP-1 Analog)",
 overview: `Semaglutide is one of the most talked-about research peptides in the world
 right now — the GLP-1 (glucagon-like peptide-1) analog behind massive advances in
 appetite regulation and blood-sugar research. It's engineered to last a full week in
 the body, so studies can run on once-weekly dosing schedules.`,
 researchApplication: `Extensively used in study models of appetite regulation,
 food intake, blood-sugar control, and body-composition change. The gold-standard
 GLP-1 research tool.`,
 keyFacts: [
 "Appetite Suppression: The most-studied GLP-1 for appetite and food-intake regulation",
 "Blood Sugar: Improves glucose handling in research diabetes models",
 "Once Weekly: Long half-life supports weekly rather than daily study protocols",
 "Research Standard: The reference compound in modern metabolic-peptide studies",
 ],
  },

 "tirzepatide": {
 form: "peptide",
 fullName: "Tirzepatide (Dual GLP-1 / GIP Analog)",
 overview: `Tirzepatide is Semaglutide's newer, dual-acting cousin — instead of hitting
 just one gut-hormone receptor (GLP-1), it hits both GLP-1 AND GIP at the same time.
 In head-to-head research it consistently outperforms single-receptor GLP-1 analogs on
 appetite, blood sugar, and body-composition endpoints. It's the new benchmark.`,
 researchApplication: `Research work on dual incretin-receptor biology, appetite
 and food-intake regulation, glucose homeostasis, and body-composition studies. The
 compound most head-to-head studies now compare Semaglutide against.`,
 keyFacts: [
 "Dual Action: Hits GLP-1 AND GIP receptors — a first for the research peptide market",
 "Stronger Response: Outperforms single-receptor GLP-1 analogs in comparative studies",
 "Once Weekly: Long-acting, supports weekly study protocols",
 "New Benchmark: The current gold standard in metabolic peptide research",
 ],
  },

 "retatrutide": {
 form: "peptide",
 fullName: "Retatrutide (Triple-Agonist GLP-1 / GIP / Glucagon)",
 overview: `Retatrutide is the newest and most ambitious peptide in the GLP-1 family —
 a TRIPLE agonist that hits GLP-1, GIP, and glucagon receptors all at once. Early
 research results suggest it produces the most dramatic changes in the entire GLP-1
 research space to date. Still relatively new to the market.`,
 researchApplication: `Cutting-edge studies on multi-receptor incretin
 biology, energy expenditure (the glucagon component uniquely increases metabolic
 rate), and body-composition studies. Represents the leading edge of this research
 area.`,
 keyFacts: [
 "Triple Receptor: The only research peptide hitting GLP-1 + GIP + glucagon simultaneously",
 "Metabolism Boost: The glucagon component uniquely increases energy expenditure",
 "Strongest Results: Research outcomes exceed both Semaglutide and Tirzepatide",
 "Newest Kid: The most recent addition to the metabolic-peptide research family",
 ],
  },

 "aod-9604": {
 form: "peptide",
 fullName: "AOD-9604 (hGH Lipolytic Fragment)",
 overview: `AOD-9604 is a small fragment of the growth-hormone molecule engineered
 specifically to keep only the fat-burning parts of GH's activity — without the
 growth or IGF-1 side effects. Think of it as "GH's fat-loss piece, isolated."`,
 researchApplication: `Widely used in studies on fat-cell
 breakdown (lipolysis), adipose tissue metabolism, and — as a secondary area —
 cartilage repair. Doesn't touch the growth-hormone receptor, so no IGF-1 spikes.`,
 keyFacts: [
 "Fat-Burning Focus: Engineered to keep only GH's lipolytic activity",
 "No Growth Effects: Doesn't activate the GH receptor — no IGF-1 side changes",
 "Small Fragment: Just 16 amino acids of the parent GH molecule",
 "Cartilage Research: Secondary literature on joint / cartilage repair models",
 ],
  },

 /* ─────────── NEURO / COGNITION ─────────── */
 "selank": {
 form: "peptide",
 fullName: "Selank",
 overview: `Selank is a Russian-designed research peptide known for its calming,
 anti-anxiety effects without the sedation or memory-impairment of traditional
 anti-anxiety drugs. It's a stabilized synthetic version of tuftsin — a naturally occurring
 immunomodulatory peptide.`,
 researchApplication: `Widely used in studies on anxiety, stress-response
 modulation, BDNF signaling, and immune-nervous system crosstalk. Most of the older
 literature is in Russian; English-language work is a smaller, growing body.`,
 keyFacts: [
 "Anti-Anxiety Focus: Best-known for calming effects without sedation in study models",
 "Non-Sedating: Doesn't cause the drowsiness or memory issues of traditional anti-anxiety drugs",
 "Russian Origin: Extensive older literature in Russian neuroscience journals",
 "Immune Crosstalk: Also modulates immune signaling — the tuftsin heritage showing through",
 ],
  },

 "semax": {
 form: "peptide",
 fullName: "Semax",
 overview: `Semax is Selank's cognition-focused sibling from the same Russian research
 program. Instead of calming, Semax is studied for sharpening — attention, memory,
 neuroprotection. It's a modified fragment of ACTH designed to survive breakdown and
 cross into the brain.`,
 researchApplication: `Widely used in studies on cognition, BDNF/TrkB signaling,
 brain plasticity, and stroke protection. Extensively studied in
 the Russian neuroscience corpus.`,
 keyFacts: [
 "Cognition Focus: Attention, memory, and mental-clarity effects in study models",
 "BDNF Booster: Studied for elevating brain-derived neurotrophic factor",
 "Neuroprotective: Rodent stroke-model literature shows protective effects",
 "Nasal Preferred: Most research protocols use intranasal delivery",
 ],
  },

 "dsip": {
 form: "peptide",
 fullName: "DSIP (Delta Sleep-Inducing Peptide)",
 overview: `DSIP is the naturally occurring "sleep peptide" — first isolated in the
 1970s from animals during their deepest sleep stages. Unlike sleep drugs that force
 unconsciousness, DSIP is studied for supporting the body's natural transition into
 deep, restorative sleep phases.`,
 researchApplication: `Widely used in studies on sleep architecture, circadian
 rhythm, stress-hormone modulation, and hormone-axis regulation. Mechanism
 is still incompletely understood — that mystery is part of what keeps researchers
 interested.`,
 keyFacts: [
 "Sleep Support: Best-known for supporting deep, restorative sleep stages in study models",
 "Naturally Occurring: Your body already produces this peptide during deep sleep",
 "Stress Modulation: Also studied for calming the stress-hormone (HPA) axis",
 "Small & Simple: Just 9 amino acids — very water-soluble and easy to work with",
 ],
  },

 /* ─────────── MELANOCORTIN FAMILY ─────────── */
 "melanotan-2": {
 form: "peptide",
 fullName: "Melanotan-2 (MT-2)",
 overview: `Melanotan-2 is the research peptide most known for stimulating pigment-
 producing cells — the melanocortin system. It's the parent compound that PT-141
 (Bremelanotide) was later derived from. Both work on the same family of receptors
 but MT-2 covers a broader range of them.`,
 researchApplication: `Widely used in studies on the melanocortin
 receptor family (MC1R through MC5R) — pigmentation biology, appetite regulation,
 and sexual-behavior brain circuits are the most-studied areas.`,
 keyFacts: [
 "Melanocortin Broad Agonist: Activates the full MC1R–MC5R receptor family",
 "Pigmentation Research: Best-known for stimulating melanin production in study models",
 "Parent of PT-141: The compound from which the more selective PT-141 was later derived",
 "Cyclic & Stable: Ring structure makes it much more durable than natural α-MSH",
 ],
  },

 "pt-141": {
 form: "peptide",
 fullName: "PT-141 (Bremelanotide)",
 overview: `PT-141 is Melanotan-2's more focused cousin — the melanocortin receptor
 agonist most known for its role in sexual-response and libido research. It has a
 much more selective receptor profile than MT-2, which means fewer collateral
 effects on pigmentation and appetite.`,
 researchApplication: `Research on sexual-response brain circuits,
 central nervous system melanocortin signaling, and MC3R/MC4R receptor biology.
 The go-to research tool in the libido-research space.`,
 keyFacts: [
 "Libido Focus: The most-studied melanocortin peptide for sexual-response research",
 "Selective Receptor: Focuses on MC3R/MC4R more than MT-2's pan-receptor activity",
 "Nasal Delivery: Research protocols often use intranasal administration",
 "FDA-Approved Sibling: The clinical version is an FDA-approved sexual-dysfunction drug",
 ],
  },

 "kpv": {
 form: "peptide",
 fullName: "KPV (Anti-Inflammatory Tripeptide)",
 overview: `KPV is a tiny three-amino-acid piece of the α-MSH hormone — the end fragment
 that scientists discovered retains most of α-MSH's inflammation-calming effects. It's
 one of the smallest research peptides on the market, which makes it exceptionally
 water-soluble and easy to work with.`,
 researchApplication: `Research research on inflammation, gut lining repair (rodent
 colitis models), and dermal wound healing. Its very small size gives it unusual
 properties for a peptide — including reported oral bioavailability in rodents.`,
 keyFacts: [
 "Anti-Inflammatory: Best-known for calming inflammation in gut and skin study models",
 "Gut Lining Focus: Rodent colitis and IBD-model literature is where KPV shines",
 "Tiny Peptide: Just 3 amino acids — the shortest peptide typically stocked",
 "Highly Soluble: Dissolves easily thanks to its small, water-loving structure",
 ],
  },

 /* ─────────── SPECIALTY / OTHER PEPTIDES ─────────── */
 "kisspeptin-10": {
 form: "peptide",
 fullName: "Kisspeptin-10",
 overview: `Kisspeptin-10 is the "master switch" research peptide for the reproductive
 hormone system. It sits at the very top of the hormonal chain and signals downstream
 neurons to release the hormones that ultimately drive LH, FSH, testosterone, and
 estrogen release.`,
 researchApplication: `Research and lab work on the reproductive
 endocrine axis, GnRH neuron activation, and puberty/fertility research. It's the
 upstream trigger of the entire hormonal reproductive cascade.`,
 keyFacts: [
 "Reproductive Axis: Master trigger for the hormonal chain that produces LH, FSH, and sex hormones",
 "Short & Active: Just the last 10 amino acids of the parent KISS1 protein — full activity retained",
 "Upstream of GnRH: Sits ABOVE GnRH in the reproductive signaling hierarchy",
 "Puberty Research: Central research tool in reproductive-development studies",
 ],
  },

 "mots-c": {
 form: "peptide",
 fullName: "MOTS-c (Mitochondrial-Derived Peptide)",
 overview: `MOTS-c is a fascinating research peptide — it's one of the few known peptides
 encoded not in your regular DNA but in mitochondrial DNA. That mitochondrial origin
 is why it's the most-studied research compound for cellular energy production and
 metabolic health.`,
 researchApplication: `Widely used in studies on mitochondrial function, cellular
 energy (ATP) production, insulin sensitivity, and metabolic health. A newer entry to
 the peptide-research world with a rapidly growing literature.`,
 keyFacts: [
 "Mitochondrial Origin: One of the only known peptides encoded in mitochondrial DNA",
 "Energy Production: Central to studies on cellular ATP and metabolic capacity",
 "Insulin Sensitivity: Research work shows improved glucose handling",
 "Small & Fast: Just 16 amino acids — moves quickly through the body",
 ],
  },

 "aicar": {
 form: "peptide",
 fullName: "AICAR (AMPK Activator)",
 overview: `AICAR is the research market's "exercise mimetic" — a compound that turns on
 the same energy-sensing pathway (AMPK) that vigorous exercise activates in your
 muscles. Researchers use it to study what would happen if you could get some of
 exercise's biochemical effects without the actual workout.`,
 researchApplication: `Widely used in studies on AMPK activation, endurance capacity,
 glucose uptake, and mitochondrial biogenesis. A staple tool in the "exercise mimetic"
 research area.`,
 keyFacts: [
 "Exercise Mimetic: Activates the same AMPK pathway that intense exercise triggers",
 "Endurance Research: Boosted running performance in classic research mouse studies",
 "Glucose Uptake: Studied for improving muscle glucose handling",
 "Mitochondrial Boost: Encourages the body to build more energy factories inside cells",
 ],
  },

 "slu-pp-332": {
 form: "small-molecule",
 fullName: "SLU-PP-332 (ERR Agonist)",
 overview: `SLU-PP-332 is a small-molecule research compound — not technically a peptide —
 that's grabbed attention as another "exercise-in-a-vial" candidate. It activates the
 ERR (estrogen-related receptor) family, which controls how muscle cells generate
 and use energy.`,
 researchApplication: `Widely used in studies on ERR receptor biology,
 endurance performance, fat metabolism, and metabolic health. Newer to the research-
 chemical market with a fast-growing literature.`,
 keyFacts: [
 "Exercise Mimetic: Another candidate in the 'workout without working out' research area",
 "ERR Activator: Targets estrogen-related receptors that control muscle energy metabolism",
 "Endurance Studies: Boosted running distance in research mouse endurance protocols",
 "Small Molecule: Not a peptide — orally stable and easier to handle than most SKUs here",
 ],
  },

 "nad-plus": {
 form: "coenzyme",
 fullName: "NAD+ (Nicotinamide Adenine Dinucleotide)",
 overview: `NAD+ isn't a peptide at all — it's a coenzyme that every cell in your body
 uses to produce energy and repair DNA. NAD+ levels decline sharply with age, which
 is why replenishing it has become one of the most-researched anti-aging strategies
 in the last decade.`,
 researchApplication: `Widely used in studies on cellular energy
 metabolism, DNA repair pathways, sirtuin activation, and the aging-repair axis.
 Central to modern "healthy aging" research programs.`,
 keyFacts: [
 "Energy Currency: Powers thousands of essential enzyme reactions inside every cell",
 "Age-Decline: Natural NAD+ levels drop noticeably as we get older",
 "DNA Repair: Activates the sirtuin enzymes that maintain and repair cellular DNA",
 "Not a Peptide: Coenzyme molecule — handled and dosed differently than peptide SKUs",
 ],
  },

 "igf-1lr3": {
 form: "peptide",
 fullName: "IGF-1 LR3 (Long-Acting IGF-1 Analog)",
 overview: `IGF-1 LR3 is a modified, longer-lasting version of insulin-like growth factor 1
 — the downstream hormone that growth hormone tells your liver to produce. The "LR3"
 modification (added chemical extension + one amino-acid swap) makes it much less
 likely to be captured by the buffers that normally trap free IGF-1, so it stays active for hours
 instead of minutes.`,
 researchApplication: `Widely used in studies on muscle protein
 synthesis, cell growth, tissue repair, and the IGF-1 axis. Considered the "downstream
 ending" of the growth-hormone-releaser story.`,
 keyFacts: [
 "Long-Acting IGF-1: Modified to stay active far longer than natural IGF-1",
 "Muscle Growth: Central research tool for muscle-protein-synthesis studies",
 "Binding-Protein Resistant: Skips the buffers that normally trap free IGF-1",
 "Direct Downstream: What GH ultimately signals your body to produce",
 ],
  },

 "glow": {
 form: "blend",
 fullName: "GLOW Blend (GHK-Cu + BPC-157 + TB-500)",
 overview: `GLOW is a pre-blended research kit combining three of the most-studied
 skin- and tissue-repair peptides in one vial: GHK-Cu (skin regeneration + copper
 delivery), BPC-157 (localized healing), and TB-500 (systemic tissue repair). The
 combination is popular in dermatology and aesthetic-research protocols.`,
 researchApplication: `Research research on skin regeneration, collagen production,
 wound healing, and tissue remodeling. Convenient co-administration format for
 protocols that would otherwise require reconstituting three separate vials.`,
 keyFacts: [
 "Triple-Peptide Skin Kit: GHK-Cu + BPC-157 + TB-500 pre-blended in one vial",
 "Skin & Collagen: All three components have strong skin/collagen research profiles",
 "One-Vial Convenience: No juggling three reconstitutions or measuring three ratios",
 "Aesthetic Research: Popular in dermatology and cosmetic-peptide study designs",
 ],
  },

 "klow": {
 form: "blend",
 fullName: "KLOW Blend (KPV + GHK-Cu + BPC-157 + TB-500)",
 overview: `KLOW takes the GLOW blend one step further — it adds KPV (anti-inflammatory
 tripeptide) to the GHK-Cu + BPC-157 + TB-500 formula. The extra KPV makes it more
 inflammation-focused, which is why researchers use it in study protocols where
 calming inflammation is as important as tissue repair.`,
 researchApplication: `Research research on inflammatory skin conditions, wound
 healing, tissue remodeling, and combined anti-inflammatory + repair pathways. The
 four-peptide format saves considerable reconstitution time.`,
 keyFacts: [
 "Four-Peptide Blend: KPV + GHK-Cu + BPC-157 + TB-500 in one vial",
 "Inflammation + Repair: KPV adds anti-inflammatory action to the GLOW base",
 "Skin & Gut Focus: Both anti-inflammation targets show up strongly in studies",
 "Convenience Kit: Save four separate reconstitutions with a single blended vial",
 ],
  },

 "glutathione": {
 form: "small-molecule",
 fullName: "L-Glutathione (Reduced GSH)",
 overview: `Glutathione is the master antioxidant your body makes on its own — a small
 three-amino-acid molecule that every cell uses to neutralize free radicals and
 handle oxidative stress. It's the most abundant antioxidant inside your cells and
 the biochemistry world's default reference point for redox research.`,
 researchApplication: `Research redox-biology, oxidative-stress, detoxification, and
 mitochondrial-function research. Used both as a reference standard and as an
 experimental supplement in lab rescue studies.`,
 keyFacts: [
 "Master Antioxidant: Your cells' most abundant homemade antioxidant molecule",
 "Detox Support: Central to the body's Phase II detoxification enzymes",
 "Redox Reference: The gold-standard measurement for cellular oxidative status",
 "Tiny Tripeptide: Just 3 amino acids — but one of your body's most-used molecules",
 ],
  },

 "snap-8": {
 form: "peptide",
 fullName: "SNAP-8 (Acetyl Glutamyl Octapeptide-3)",
 overview: `SNAP-8 is a topical research peptide developed in the cosmetic-dermatology
 world as a milder, peptide-based alternative to the mechanism behind Botox. It
 interferes with the same nerve-signaling process at the skin's surface — but through
 a completely different (non-toxic) chemistry.`,
 researchApplication: `Research dermatology and skin-permeability research on
 expression-line reduction, dermal peptide delivery, and neuromuscular junction
 biology. A close relative of Argireline in this research space.`,
 keyFacts: [
 "Topical Focus: Designed specifically for dermal / skin-surface research applications",
 "Botox-Adjacent: Studies the same nerve-signaling pathway with milder peptide chemistry",
 "Expression Lines: Best-known for research on forehead / eye expression-line areas",
 "Cousin to Argireline: Related mechanism, slightly different amino-acid composition",
 ],
  },

 /* ─────────── LAB SUPPLIES ─────────── */
 "bacteriostatic-water": {
 form: "solution",
 fullName: "Bacteriostatic Water (0.9% Benzyl Alcohol)",
 overview: `Bacteriostatic water is sterile water with a preservative (0.9% benzyl
 alcohol) added — the standard reconstitution solvent for freeze-dried
 research peptides. Unlike plain sterile water, it prevents bacterial growth after
 the vial is opened, so you can dip in multiple times over 28 days.`,
 researchApplication: `Standard laboratory diluent for reconstituting freeze-dried
 peptide research SKUs. The 0.9% benzyl alcohol preservative keeps the reconstituted
 solution viable across repeated withdrawals for up to 28 days.`,
 keyFacts: [
 "Reconstitution Standard: The universal solvent for freeze-dried peptide vials",
 "Multi-Use: 28-day post-opening usable window thanks to the preservative",
 "Preservative: 0.9% benzyl alcohol — bacteriostatic, not bactericidal",
 "Room Temperature Stable: No refrigeration required — store 15-30°C",
 ],
  },

 "acetic-acid": {
 form: "solution",
 fullName: "Acetic Acid (Reconstitution Solvent)",
 overview: `Acetic acid is a specialty reconstitution solvent used for a small handful
 of research peptides (notably TB-500 / Thymosin β4) that don't dissolve cleanly in
 plain bacteriostatic water. The mild acidity helps break the peptide out of its
 folded state so it goes fully into solution.`,
 researchApplication: `Reconstitution solvent for research peptides that resist standard
 bacteriostatic water. Most commonly used for TB-500 preparation in research
 research protocols.`,
 keyFacts: [
 "Specialty Solvent: For peptides that don't fully dissolve in bacteriostatic water",
 "TB-500 Standard: The most common reconstitution solvent for Thymosin β4 preparation",
 "Mild Acidity: Just acidic enough to help unfold and dissolve stubborn peptides",
 "Small Volumes: A little goes a long way — typical protocols use tiny amounts",
 ],
  },

 "alcohol-prep-pads": {
 form: "pad",
 fullName: "Alcohol Prep Pads (Isopropyl 70%)",
 overview: `Individually sealed 70% isopropyl alcohol prep pads — the standard sanitation
 supply every research lab keeps on hand. Used to sanitize vial stoppers before
 withdrawing solution and to keep work surfaces clean during reconstitution.`,
 researchApplication: `Laboratory sanitation supply for vial-stopper cleaning prior to
 solvent withdrawal, work-surface sanitation, and general clean-technique laboratory
 practice.`,
 keyFacts: [
 "70% Isopropyl: Standard laboratory sanitation concentration",
 "Individually Sealed: Each pad in its own foil packet — sterile until opened",
 "Vial Prep: Wipe the stopper before every withdrawal for clean technique",
 "Lab Essential: Every research bench keeps a box within reach",
 ],
  },

 /* ─────────── PROSOURCE MIST — nasal spray line ─────────── */
 "selank-nasal-spray": {
 form: "nasal-spray",
 fullName: "Selank Nasal Spray (10mg / 5mL)",
 overview: `Selank in a ready-to-use nasal spray — the same calming research peptide
 people already know, now delivered through a metered mist bottle instead of a
 syringe. No reconstitution, no mixing, no needles. Uncap, prime, and go.`,
 researchApplication: `Studied for calm, non-sedating focus and stress-response
 support. The nasal spray format is the delivery route most commonly used in
 the published Selank literature and is ideal for protocols that call for
 multiple daily administrations without the friction of injections.`,
 keyFacts: [
 "Zero Prep: Comes ready to use — no vials to mix, no syringes to fill",
 "Multi-Dose Friendly: Metered pump makes repeat daily dosing effortless",
 "Nasal Delivery: The delivery route featured in most published Selank research",
 "5mL Bottle: 10mg total peptide across roughly 50 metered sprays",
 ],
  },

 "semax-nasal-spray": {
 form: "nasal-spray",
 fullName: "Semax Nasal Spray (10mg / 5mL)",
 overview: `Semax in a ready-to-use nasal spray — the cognition-focused sibling to
 Selank, now in a needle-free spray bottle. Prime the pump, one quick spray, done.
 Ideal for research protocols that repeat throughout the day.`,
 researchApplication: `Studied for attention, memory, and mental-clarity effects,
 with the intranasal route being standard across the published Semax literature.
 The spray format removes reconstitution and syringe steps entirely — a big
 convenience win for multi-dose research days.`,
 keyFacts: [
 "Zero Prep: No vials, no mixing, no syringes — spray and go",
 "Made for Multi-Dose: Metered pump handles repeat administration throughout the day",
 "Standard Delivery Route: Intranasal is how Semax is used in the research literature",
 "5mL Bottle: 10mg of Semax across approximately 50 metered sprays",
 ],
  },

 "mots-c-nasal-spray": {
 form: "nasal-spray",
 fullName: "MOTS-c Nasal Spray (10mg / 5mL)",
 overview: `MOTS-c in a ready-to-use nasal spray — the mitochondrial research
 peptide, now delivered through a metered spray bottle. No injections, no mixing,
 no reconstitution steps between doses.`,
 researchApplication: `Studied in metabolic and mitochondrial research — cellular
 energy production, insulin sensitivity, and endurance-related work. The
 needle-free spray format makes it especially practical for daily or multi-daily
 research protocols without adding syringe overhead.`,
 keyFacts: [
 "Zero Prep: Ready-to-use metered spray — no reconstitution needed",
 "Multi-Dose Friendly: Convenient for research designs with repeated daily administration",
 "Needle-Free: Skips the injection step entirely",
 "5mL Bottle: 10mg of MOTS-c across approximately 50 metered sprays",
 ],
  },

 "nad-plus-nasal-spray": {
 form: "nasal-spray",
 fullName: "NAD+ Nasal Spray (500mg / 5mL)",
 overview: `NAD+ in a ready-to-use nasal spray — the cellular-energy coenzyme,
 now delivered in a needle-free metered spray bottle. No mixing, no injections,
 no bulky IV setup. Just uncap, prime, and spray.`,
 researchApplication: `Studied in longevity, cellular energetics, DNA repair, and
 sirtuin activation research. The spray format is designed for the kind of daily
 (or multiple-times-daily) protocols that NAD+ research often calls for — with
 zero syringe or IV workflow between doses.`,
 keyFacts: [
 "Zero Prep: Comes ready to use — no mixing, no injections, no IV setup",
 "Made for Daily Use: Metered pump built for repeat administration throughout the day",
 "Higher Dose: 500mg of NAD+ per 5mL bottle",
 "Not a Peptide: NAD+ is a coenzyme — different from the peptide sprays but delivered the same way",
 ],
  },

 "promist-bundle": {
 form: "nasal-spray",
 fullName: "ProSource Mist Bundle (Selank + Semax + MOTS-c + NAD+)",
 overview: `The full ProSource Mist lineup in a single bundle — one bottle each of
 Selank, Semax, MOTS-c, and NAD+ nasal spray. Everything is ready to use out of
 the box: no reconstitution, no syringes, no mixing. Bundle pricing saves you
 versus buying each bottle individually.`,
 researchApplication: `Designed for researchers who want the full ProSource Mist
 lineup on hand — calming (Selank), cognition (Semax), mitochondrial energy
 (MOTS-c), and cellular NAD+ support — all in the same convenient spray format
 for stacked or comparative research protocols. Zero prep between bottles.`,
 keyFacts: [
 "Complete Line: All four ProSource Mist bottles in one order — Selank, Semax, MOTS-c, NAD+",
 "Bundle Savings: Priced below the sum of individual bottles",
 "Same Format Across All Four: Metered spray, no reconstitution, no needles",
 "Stack-Ready: Convenient for research protocols that combine multiple compounds",
 ],
  },

 /* ─────────── SKINCARE — Blue Renewal Multi-Peptide Serum ─────────── */
 // Cosmetic product framing — NO research-use disclaimer, cosmetic language
 // throughout (appearance, hydration, texture — not "research", "study",
 // or therapeutic claims). Owner-specified positioning to keep the product
 // distinct from the research peptide catalog.
 "blue-renewal": {
 form: "cosmetic",
 fullName: "Blue Renewal\u2122 Multi-Peptide Serum (30 mL / 1 fl oz)",
 // overview becomes the meta description — leads with the exact search
 // phrase ("Blue Renewal multi-peptide serum") plus cosmetic benefit
 // keywords Google/AI tools pattern-match on for skincare intent
 // (facial serum, wrinkles, fine lines, skin brightening, tightening).
 overview: `Blue Renewal\u2122 is a professional-strength multi-peptide facial serum
 formulated to help improve the appearance of smoother, firmer, and healthier-
 looking skin — softening the look of fine lines and wrinkles while supporting
 hydration and skin brightening. A premium blend of 2% GHK-Cu (Copper
 Tripeptide-1) and 3% SNAP-8 (Acetyl Octapeptide-3) works alongside hyaluronic
 acid, niacinamide, and panthenol to enhance the appearance of elasticity,
 texture, and skin tone. Housed in a 30 mL frosted-glass airless pump bottle
 for precise daily application.`,
 researchApplication: `Blue Renewal\u2122 combines modern cosmetic peptide
 technology with proven skin-conditioning ingredients — the same category of
 formulation used in premium anti-aging and wrinkle-appearance skincare
 products. The 2% GHK-Cu copper peptide and 3% SNAP-8 octapeptide-3 are
 supported by hyaluronic acid (for a plumper, more refreshed look), niacinamide
 (for a brighter, more even complexion), panthenol (for a healthy skin
 barrier), and beta-glucan (for soothing hydration). A lightweight face serum
 designed to sit comfortably under daily moisturizer and sunscreen.
 Manufactured in the USA at ProSource Labs in Corona, California.`,
 keyFacts: [
 "Professional Strength: 2% GHK-Cu (Copper Tripeptide-1) + 3% SNAP-8 (Acetyl Octapeptide-3)",
 "Fine Line & Wrinkle Appearance: Peptide complex helps soften the look of expression lines and support firmer-looking skin",
 "Hydration Complex: Hyaluronic acid, niacinamide, panthenol, beta-glucan, sodium PCA for lasting hydration and skin brightening",
 "Airless Pump: 30 mL frosted-glass bottle preserves the formula and delivers precise application",
 "APD\u00ae Professional Formula: Manufactured in the USA by ProSource Labs, Corona, California",
 ],
 // Explicit consumer-facing skincare keywords — replaces the peptide-
 // flavored auto-generated set. Covers the exact search intents shoppers
 // use: face serum, copper peptide serum, wrinkle serum, fine lines,
 // skin brightening, tightening, anti-aging, GHK-Cu, SNAP-8. Google and
 // AI answer engines (ChatGPT / Perplexity / Claude) index these.
 keywords: [
   "Blue Renewal serum",
   "multi-peptide facial serum",
   "copper peptide serum",
   "GHK-Cu serum",
   "SNAP-8 serum",
   "facial serum",
   "face serum",
   "face cream",
   "peptide face serum",
   "professional strength skincare",
   "fine line serum",
   "wrinkle serum",
   "wrinkle appearance",
   "skin brightening serum",
   "skin tightening serum",
   "skin firming serum",
   "anti-aging peptide serum",
   "hyaluronic acid serum",
   "niacinamide serum",
   "airless pump serum",
   "made in USA skincare",
   "ProSource Labs skincare",
 ].join(", "),
 // Cosmetic-specific fields for the PDP template
 directions: `Apply 1\u20132 pumps to clean, dry skin on the face and neck morning and evening. Allow the serum to absorb before applying moisturizer. During daytime use, follow with a broad-spectrum sunscreen.`,
 ingredients: `Aqua (Water), Glycerin, Propanediol, Copper Tripeptide-1 (GHK-Cu), Acetyl Octapeptide-3 (SNAP-8), Palmitoyl Tripeptide-1, Palmitoyl Tetrapeptide-7, Sodium Hyaluronate, Niacinamide, Panthenol (Pro-Vitamin B5), Allantoin, Beta-Glucan, Sodium PCA, Xanthan Gum, Phenoxyethanol, Ethylhexylglycerin.`,
 storage: `Store in a cool, dry place away from direct sunlight. Replace the protective cap after each use.`,
  },
};

/**
 * Normalize product form based on meta.form OR product.category OR slug.
 * Used by ProductLongContent.jsx to pick the right template variant.
 */
export function getProductForm(product) {
 const meta = PRODUCT_META[product?.slug];
 if (meta?.form) return meta.form;
 const cat = (product?.category || "").toLowerCase();
 const slug = (product?.slug || "").toLowerCase();
 if (cat.includes("skincare") || cat.includes("cosmeceutical") || slug === "blue-renewal") return "cosmetic";
 if (cat.includes("prosource mist") || slug.includes("nasal-spray") || slug === "promist-bundle") return "nasal-spray";
 if (cat.includes("supplies") || slug.includes("bacteriostatic") || slug.includes("acetic")) return "solution";
 if (slug.includes("prep-pad") || slug.includes("alcohol-pad")) return "pad";
 if (slug.includes("nad") || slug.includes("+")) return "coenzyme";
 if (slug === "hgh-191aa" || slug === "hgh") return "protein";
 return "peptide";
}

export function getProductMeta(slug) {
 return PRODUCT_META[slug] || null;
}

/**
 * Product-specific FAQ generator. Returns an array of {q, a} pairs sized to
 * each product's form. Form-aware so peptides get peptide questions, lab
 * supplies get lab-supply questions. Keeps the tone in line with the
 * consumer-friendly overview copy above — no medical claims, benefit-first.
 */
export function getProductFAQs(product) {
 const slug = product?.slug || "";
 const name = product?.name || slug;
 const meta = PRODUCT_META[slug] || {};
 const form = meta.form || getProductForm(product);
 const category = product?.category || "Research Peptide";

 if (form === "solution") {
 return [
 {
 q: `What is ${name}?`,
 a: meta.overview || `${name} is a laboratory-grade reagent solution used in research workflows.`,
 },
 {
 q: `How is ${name} typically used in research?`,
 a: meta.researchApplication || `${name} is a standard laboratory reagent.`,
 },
 {
 q: `How should ${name} be stored?`,
 a: `Store ${name} at room temperature (15–30°C), tightly capped and away from direct light. Refer to the batch documentation shipped with each vial for the specific handling window.`,
 },
 {
 q: `Is ${name} intended for human use?`,
 a: `No. ${name} is offered strictly for laboratory research applications and is not intended for human or veterinary consumption.`,
 },
 ];
 }
 if (form === "pad") {
 return [
 {
 q: `What are ${name} used for?`,
 a: meta.overview || `${name} are individually sealed 70% isopropyl alcohol prep pads for laboratory sanitation.`,
 },
 {
 q: `Are ${name} sterile?`,
 a: `Each pad is individually foil-sealed and sterile until opened. Discard after single use.`,
 },
 {
 q: `Where do ${name} ship from?`,
 a: `All ProSource Labs orders ship from our Southern California facility with tracked domestic delivery.`,
 },
 ];
 }
 if (form === "cosmetic") {
 return [
 {
 q: `What is ${name}?`,
 a: meta.overview || `${name} is a professional-strength multi-peptide facial serum with 2% GHK-Cu and 3% SNAP-8, designed to help improve the appearance of smoother, firmer, healthier-looking skin.`,
 },
 {
 q: `How do I use ${name}?`,
 a: meta.directions || `Apply 1\u20132 pumps to clean, dry skin on the face and neck morning and evening. Allow the serum to absorb before applying moisturizer. During daytime use, follow with a broad-spectrum sunscreen.`,
 },
 {
 q: `What peptides are in ${name}?`,
 a: `${name} features 2% GHK-Cu (Copper Tripeptide-1), 3% SNAP-8 (Acetyl Octapeptide-3), plus supporting peptides Palmitoyl Tripeptide-1 and Palmitoyl Tetrapeptide-7. The full INCI ingredient list is shown on the product page.`,
 },
 {
 q: `Is ${name} suitable for daily use?`,
 a: `Yes. ${name} is designed as a lightweight daily serum for morning and evening skincare routines. Follow with moisturizer and (in the daytime) a broad-spectrum sunscreen.`,
 },
 {
 q: `How should ${name} be stored?`,
 a: meta.storage || `Store in a cool, dry place away from direct sunlight. Replace the protective cap after each use.`,
 },
 {
 q: `Where is ${name} made?`,
 a: `${name} is manufactured in the USA by ProSource Labs in Corona, California.`,
 },
 ];
 }

 if (form === "nasal-spray") {
 return [
 {
 q: `What is ${name}?`,
 a: meta.overview || `${name} is a ready-to-use research nasal spray in the ProSource Mist line — pre-mixed, third-party tested, and delivered through a metered pump bottle. No reconstitution required.`,
 },
 {
 q: `Do I need to mix or reconstitute ${name}?`,
 a: `No. ${name} ships as a ready-to-use metered spray. No bacteriostatic water, no syringes, and no mixing — uncap, prime the pump, and it's ready.`,
 },
 {
 q: `Can ${name} be used multiple times a day?`,
 a: `Yes — the metered spray format is designed for repeat administration throughout the day, which is one of the main convenience advantages over injection-based research delivery.`,
 },
 {
 q: `How should ${name} be stored?`,
 a: `Store ${name} refrigerated (2–8°C / 36–46°F), upright, and out of direct light. Bring to room temperature briefly before use for a smoother spray. Do not freeze.`,
 },
 {
 q: `Is ${name} third-party tested?`,
 a: `Yes. Every ${name} bottle ships with a batch identifier traceable back to its manufacturing lot, and each lot is third-party tested for purity and potency.`,
 },
 {
 q: `Where does ${name} ship from?`,
 a: `All ${name} orders ship from ProSource Labs' Southern California facility with tracked domestic delivery.`,
 },
 {
 q: `Is ${name} intended for human use?`,
 a: `No. ${name} is supplied strictly for laboratory research applications and is not intended for and not approved for human or veterinary consumption.`,
 },
 ];
 }
 // Default: peptide / small-molecule / protein / coenzyme / blend
 const faqs = [
 {
 q: `What is ${name}?`,
 a: meta.overview || `${name} is a research-grade compound in the ${category} category.`,
 },
 {
 q: `What is ${name} used for in research?`,
 a: meta.researchApplication || `Refer to the research for ${name} research applications.`,
 },
 {
 q: `How is ${name} verified?`,
 a: `Every ${name} vial ships with a batch identifier for full traceability back to its manufacturing lot, an HPLC purity report, and Certificate of Analysis (COA).`,
 },
 {
 q: `How should ${name} be stored?`,
 a: `Lyophilized (powder) ${name}: short term (weeks to 12 months) store at 2–8°C (36–46°F) in a refrigerator; long term (12+ months) store at -20°C (-4°F) in a freezer. Reconstituted (in solution): in use (up to 28 days) store at 2–8°C (36–46°F) in a refrigerator. Never store reconstituted peptides in a freezer.`,
 },
 {
 q: `Where does ${name} ship from?`,
 a: `All ${name} orders ship from ProSource Labs' Southern California facility with tracked domestic delivery.`,
 },
 {
 q: `Is ${name} intended for human use?`,
 a: `No. ${name} is supplied strictly for laboratory research applications and is not intended for and not approved for human or veterinary consumption.`,
 },
 ];
 return faqs;
}
